import api from './api';

class CompanyService {
  // Get all companies with pagination and filters
  async getCompanies(params = {}) {
    try {
      const response = await api.get('/companies', { params });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Get single company by ID
  async getCompany(id) {
    try {
      const response = await api.get(`/companies/${id}`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Create new company
  async createCompany(formData) {
    try {
      const response = await api.post('/companies', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Update company
  async updateCompany(id, formData) {
    try {
      const response = await api.put(`/companies/${id}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Delete company
  async deleteCompany(id) {
    try {
      const response = await api.delete(`/companies/${id}`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Send credentials to company
  async sendCredentials(data) {
    try {
      const response = await api.post(`/companies/${data.companyId}/send-credentials`, data);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Get company statistics
  async getCompanyStats() {
    try {
      const response = await api.get('/companies/stats/overview');
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Get expiring companies
  async getExpiringCompanies(days = 30) {
    try {
      const response = await api.get('/companies', {
        params: {
          expiring: true,
          days: days
        }
      });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Bulk operations
  async bulkUpdateStatus(companyIds, status) {
    try {
      const response = await api.patch('/companies/bulk-status', {
        companyIds,
        status
      });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async bulkDelete(companyIds) {
    try {
      const response = await api.delete('/companies/bulk-delete', {
        data: { companyIds }
      });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Export companies data
  async exportCompanies(format = 'csv', filters = {}) {
    try {
      const response = await api.get('/companies/export', {
        params: {
          format,
          ...filters
        },
        responseType: 'blob'
      });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Upload company logo
  async uploadLogo(file) {
    try {
      const formData = new FormData();
      formData.append('logo', file);
      
      const response = await api.post('/upload/company-logo', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Validate company data
  validateCompanyData(data) {
    const errors = {};

    if (!data.companyName || data.companyName.trim().length < 2) {
      errors.companyName = 'Company name must be at least 2 characters';
    }

    if (!data.clientName || data.clientName.trim().length < 2) {
      errors.clientName = 'Client name must be at least 2 characters';
    }

    if (!data.phone || !/^[6-9]\d{9}$/.test(data.phone)) {
      errors.phone = 'Please enter a valid 10-digit phone number';
    }

    if (!data.email || !/\S+@\S+\.\S+/.test(data.email)) {
      errors.email = 'Please enter a valid email address';
    }

    if (!data.packageId) {
      errors.packageId = 'Please select a package';
    }

    if (!data.startDate) {
      errors.startDate = 'Please select start date';
    }

    if (!data.expiryDate) {
      errors.expiryDate = 'Please select expiry date';
    }

    if (data.startDate && data.expiryDate && new Date(data.expiryDate) <= new Date(data.startDate)) {
      errors.expiryDate = 'Expiry date must be after start date';
    }

    return {
      isValid: Object.keys(errors).length === 0,
      errors
    };
  }

  // Calculate pricing
  calculatePricing(packagePrice, discountPercent = 0, gstRate = 18) {
    const basePrice = parseFloat(packagePrice);
    const discount = Math.min(discountPercent, 50); // Max 50% discount
    const discountAmount = (basePrice * discount) / 100;
    const discountedPrice = basePrice - discountAmount;
    const gstAmount = (discountedPrice * gstRate) / 100;
    const finalPrice = discountedPrice + gstAmount;

    return {
      basePrice,
      discountPercent: discount,
      discountAmount,
      discountedPrice,
      gstRate,
      gstAmount,
      finalPrice
    };
  }

  // Generate username from company name
  generateUsername(companyName) {
    return companyName
      .toLowerCase()
      .replace(/[^a-z0-9]/g, '')
      .substring(0, 20) + '_admin';
  }

  // Generate secure password
  generatePassword(length = 12) {
    const charset = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789@#$%&*';
    let password = '';
    
    // Ensure at least one character from each category
    password += 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'[Math.floor(Math.random() * 26)]; // Uppercase
    password += 'abcdefghijklmnopqrstuvwxyz'[Math.floor(Math.random() * 26)]; // Lowercase
    password += '0123456789'[Math.floor(Math.random() * 10)]; // Number
    password += '@#$%&*'[Math.floor(Math.random() * 6)]; // Special char
    
    // Fill the rest randomly
    for (let i = password.length; i < length; i++) {
      password += charset[Math.floor(Math.random() * charset.length)];
    }
    
    // Shuffle the password
    return password.split('').sort(() => 0.5 - Math.random()).join('');
  }

  // Format company data for display
  formatCompanyData(company) {
    return {
      ...company,
      formattedPrice: new Intl.NumberFormat('en-IN', {
        style: 'currency',
        currency: 'INR'
      }).format(company.finalPrice),
      
      formattedStartDate: new Date(company.startDate).toLocaleDateString('en-IN'),
      formattedExpiryDate: new Date(company.expiryDate).toLocaleDateString('en-IN'),
      
      statusColor: this.getStatusColor(company.status),
      statusText: this.getStatusText(company.status),
      
      daysUntilExpiry: this.calculateDaysUntilExpiry(company.expiryDate),
      isExpiringSoon: this.isExpiringSoon(company.expiryDate, 30)
    };
  }

  // Get status color for UI
  getStatusColor(status) {
    const colors = {
      active: 'green',
      inactive: 'orange',
      expired: 'red',
      suspended: 'purple'
    };
    return colors[status] || 'default';
  }

  // Get status text for display
  getStatusText(status) {
    const texts = {
      active: 'Active',
      inactive: 'Inactive',
      expired: 'Expired',
      suspended: 'Suspended'
    };
    return texts[status] || 'Unknown';
  }

  // Calculate days until expiry
  calculateDaysUntilExpiry(expiryDate) {
    const today = new Date();
    const expiry = new Date(expiryDate);
    const diffTime = expiry - today;
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  }

  // Check if expiring soon
  isExpiringSoon(expiryDate, days = 30) {
    return this.calculateDaysUntilExpiry(expiryDate) <= days;
  }

  // Handle API errors
  handleError(error) {
    if (error.response) {
      // Server responded with error status
      return {
        message: error.response.data.message || 'Server error occurred',
        status: error.response.status,
        data: error.response.data
      };
    } else if (error.request) {
      // Request made but no response received
      return {
        message: 'Network error - please check your connection',
        status: 0
      };
    } else {
      // Something else happened
      return {
        message: error.message || 'An unexpected error occurred',
        status: -1
      };
    }
  }
}

export const companyService = new CompanyService();
export default companyService;